import { useMemo } from 'react';
import { getProjectIndex } from '@/utils/project-navigation-cache';
import { useLocalStorageState } from '@/hooks/ai-agents/useLocalStorageState';
import { useNavigate } from 'react-router-dom';

export interface ParsedRecommendation {
  type: 'task' | 'research' | 'page' | 'image' | 'project';
  title: string;
  description: string;
  goalId?: string;
  projectId?: string;
  pageType?: string;
  imageStyle?: string;
  imageSize?: string;
  projectType?: string;
  isValid: boolean;
  errors: string[];
}

export interface ResponseParsingResult {
  hasRecommendation: boolean;
  recommendation: ParsedRecommendation | null;
  originalMessage: string;
  conversationalResponse?: string;
  structuredContent?: string;
}

/**
 * Hook for parsing AI responses to extract recommendation data with IDs
 */
export const useResponseParsing = () => {
  
  const parseRecommendationResponse = useMemo(() => {
    return (message: string): ResponseParsingResult => {
      const errors: string[] = [];
      
      // Check for dual-format structure with recommendation delimiters
      const hasRecommendationDelimiters = message.includes('---RECOMMENDATION---') && message.includes('---END RECOMMENDATION---');
      
      let conversationalResponse = '';
      let structuredContent = '';
      let contentToCheck = message;
      
      if (hasRecommendationDelimiters) {
        // Extract conversational part (everything before ---RECOMMENDATION---)
        const recommendationStartIndex = message.indexOf('---RECOMMENDATION---');
        conversationalResponse = message.substring(0, recommendationStartIndex).trim();
        
        // Extract structured part (everything between the delimiters)
        const recommendationEndIndex = message.indexOf('---END RECOMMENDATION---');
        const structuredStart = recommendationStartIndex + '---RECOMMENDATION---'.length;
        structuredContent = message.substring(structuredStart, recommendationEndIndex).trim();
        
        // Use structured content for parsing
        contentToCheck = structuredContent;
      } else {
        // Fallback: treat entire message as both conversational and structured for backward compatibility
        conversationalResponse = message;
        structuredContent = message;
        contentToCheck = message;
      }
      
      // Check if content contains recommendation format
      const hasTaskFormat = contentToCheck.includes('TASK TITLE:') && contentToCheck.includes('TASK DESCRIPTION:');
      const hasResearchFormat = contentToCheck.includes('RESEARCH TITLE:') && contentToCheck.includes('RESEARCH DESCRIPTION:');
      const hasPageFormat = contentToCheck.includes('PAGE TITLE:') && contentToCheck.includes('PAGE DESCRIPTION:');
      const hasImageFormat = contentToCheck.includes('IMAGE DESCRIPTION:') && contentToCheck.includes('IMAGE STYLE:');
      const hasProjectFormat = contentToCheck.includes('PROJECT TITLE:') && contentToCheck.includes('PROJECT DESCRIPTION:');
      
      if (!hasTaskFormat && !hasResearchFormat && !hasPageFormat && !hasImageFormat && !hasProjectFormat) {
        return {
          hasRecommendation: false,
          recommendation: null,
          originalMessage: message,
          conversationalResponse: hasRecommendationDelimiters ? conversationalResponse : message,
          structuredContent: hasRecommendationDelimiters ? structuredContent : ''
        };
      }
      
      // Get available IDs for validation
      const projectIndex = getProjectIndex();
      const availableGoalIds = projectIndex?.projects?.map(p => p.goal?.id).filter(Boolean) || [];
      const availableProjectIds = projectIndex?.projects?.map(p => p.id).filter(Boolean) || [];
      
      let type: 'task' | 'research' | 'page' | 'image' | 'project';
      let title = '';
      let description = '';
      let goalId: string | undefined;
      let projectId: string | undefined;
      let pageType: string | undefined;
      let imageStyle: string | undefined;
      let imageSize: string | undefined;
      let projectType: string | undefined;
      
      try {
        if (hasTaskFormat) {
          type = 'task';
          title = extractField(contentToCheck, 'TASK TITLE');
          description = extractField(contentToCheck, 'TASK DESCRIPTION');
          // No longer extract or validate goalId for tasks - they go to personal task manager
          
          if (!title) errors.push('Missing task title');
          if (!description) errors.push('Missing task description');
          
        } else if (hasResearchFormat) {
          type = 'research';
          title = extractField(contentToCheck, 'RESEARCH TITLE');
          description = extractField(contentToCheck, 'RESEARCH DESCRIPTION');
          // No longer extract or validate goalId for research - they go to private research library
          
          console.log(`[ResponseParsing] Research validation:`, {
            title: title || '[EMPTY]',
            description: description || '[EMPTY]'
          });
          
          if (!title) errors.push('Missing research title');
          if (!description) errors.push('Missing research description');
          
        } else if (hasPageFormat) {
          type = 'page';
          title = extractField(contentToCheck, 'PAGE TITLE');
          description = extractField(contentToCheck, 'PAGE DESCRIPTION');
          pageType = extractField(contentToCheck, 'PAGE TYPE');
          // No longer extract or validate projectId for pages - they go to private text library
          
          console.log(`[ResponseParsing] Page validation:`, {
            title: title || '[EMPTY]',
            description: description || '[EMPTY]',
            pageType: pageType || '[EMPTY]'
          });
          
          if (!title) errors.push('Missing page title');
          if (!description) errors.push('Missing page description');
          if (!pageType) errors.push('Missing PAGE TYPE');
          
        } else if (hasImageFormat) {
          type = 'image';
          description = extractField(contentToCheck, 'IMAGE DESCRIPTION');
          imageStyle = extractField(contentToCheck, 'IMAGE STYLE');
          imageSize = extractField(contentToCheck, 'IMAGE SIZE');
          // No longer extract or validate projectId for images - they go to private image library
          title = `Image: ${description?.substring(0, 50) || 'Generated Image'}`;
          
          console.log(`[ResponseParsing] Image validation:`, {
            description: description || '[EMPTY]',
            imageStyle: imageStyle || '[EMPTY]',
            imageSize: imageSize || '[EMPTY]'
          });
          
          if (!description) errors.push('Missing image description');
          if (!imageStyle) errors.push('Missing IMAGE STYLE');
          if (!imageSize) errors.push('Missing IMAGE SIZE');
          
        } else if (hasProjectFormat) {
          type = 'project';
          title = extractField(contentToCheck, 'PROJECT TITLE');
          description = extractField(contentToCheck, 'PROJECT DESCRIPTION');
          projectType = extractField(contentToCheck, 'PROJECT TYPE');
          
          console.log(`[ResponseParsing] Project validation:`, {
            title: title || '[EMPTY]',
            description: description || '[EMPTY]',
            projectType: projectType || '[EMPTY]'
          });
          
          if (!title) errors.push('Missing project title');
          if (!description) errors.push('Missing project description');
          if (!projectType) errors.push('Missing PROJECT TYPE');
          
        } else {
          throw new Error('Unknown recommendation format');
        }
        
      } catch (error) {
        errors.push(`Parsing error: ${error instanceof Error ? error.message : 'Unknown error'}`);
        console.error(`[ResponseParsing] Parsing exception:`, error);
      }
      
      const recommendation: ParsedRecommendation = {
        type: type!,
        title,
        description,
        goalId,
        projectId,
        pageType,
        imageStyle,
        imageSize,
        projectType,
        isValid: errors.length === 0,
        errors
      };
      
      console.log(`[ResponseParsing] Final parsing result:`, {
        hasRecommendation: true,
        type: type!,
        isValid: recommendation.isValid,
        errors: recommendation.errors,
        extractedData: {
          title: title || '[EMPTY]',
          description: description || '[EMPTY]',
          goalId: goalId || '[EMPTY]', // Only for projects now
          projectId: projectId || '[EMPTY]', // Only for projects now
          pageType: pageType || '[EMPTY]',
          imageStyle: imageStyle || '[EMPTY]',
          imageSize: imageSize || '[EMPTY]',
          projectType: projectType || '[EMPTY]'
        }
      });
      
      return {
        hasRecommendation: true,
        recommendation,
        originalMessage: message,
        conversationalResponse: conversationalResponse || message,
        structuredContent: structuredContent || message
      };
    };
  }, []);
  
  return {
    parseRecommendationResponse
  };
};

/**
 * Extract field value from formatted AI response
 */
function extractField(message: string, fieldName: string): string {
  const regex = new RegExp(`${fieldName}:\\s*(.+?)(?=\\n|$)`, 'i');
  const match = message.match(regex);
  const result = match ? match[1].trim() : '';
  
  // Add detailed logging for debugging
  console.log(`[ResponseParsing] Extracting field "${fieldName}":`, {
    fieldName,
    messageLength: message.length,
    messagePreview: message.substring(0, 200) + (message.length > 200 ? '...' : ''),
    regexPattern: regex.toString(),
    matchFound: !!match,
    matchResult: match ? match[1] : null,
    trimmedResult: result,
    resultLength: result.length
  });
  
  return result;
}

/**
 * Validate that an ID exists in the available options
 */
export function validateId(id: string, availableIds: string[]): boolean {
  return availableIds.includes(id);
}

/**
 * Get project title by ID for display purposes
 */
export function getProjectTitleById(projectId: string): string | null {
  const projectIndex = getProjectIndex();
  const project = projectIndex?.projects?.find(p => p.id === projectId);
  return project?.title || null;
}

/**
 * Get goal title by ID for display purposes
 */
export function getGoalTitleById(goalId: string): string | null {
  const projectIndex = getProjectIndex();
  const project = projectIndex?.projects?.find(p => p.goal?.id === goalId);
  return project?.goal?.title || null;
}

/**
 * Get project information by project ID
 */
export function getProjectInfoById(projectId: string): { title: string; goalId: string; goalTitle: string } | null {
  const projectIndex = getProjectIndex();
  const project = projectIndex?.projects?.find(p => p.id === projectId);
  if (!project || !project.goal) return null;
  
  return {
    title: project.title,
    goalId: project.goal.id,
    goalTitle: project.goal.title
  };
}

/**
 * Get project information by goal ID
 */
export function getProjectInfoByGoalId(goalId: string): { title: string; projectId: string; goalTitle: string } | null {
  const projectIndex = getProjectIndex();
  const project = projectIndex?.projects?.find(p => p.goal?.id === goalId);
  if (!project || !project.goal) return null;
  
  return {
    title: project.title,
    projectId: project.id,
    goalTitle: project.goal.title
  };
}

/**
 * Generate navigation URL for project
 */
export function getProjectNavigationUrl(projectId: string): string {
  return `/project/${projectId}/tasks`;
}

/**
 * Generate navigation URL for goal
 */
export function getGoalNavigationUrl(goalId: string): string {
  // Try to find the project ID for this goal to use new route structure
  const projectInfo = getProjectInfoByGoalId(goalId);
  
  if (projectInfo?.projectId) {
    return `/project/${projectInfo.projectId}/tasks`;
  } else {
    // Fallback to old route for backward compatibility during migration
    return `/task-manager/goal/${goalId}`;
  }
}

/**
 * Hook for navigation with modal closing (similar to useViewRecommendationNavigation)
 */
export const useNavigationWithModalClose = () => {
  const navigate = useNavigate();
  const { isModalOpen, closeModal } = useLocalStorageState();

  // Helper to close chat modal before navigation
  const navigateAndCloseModal = (url: string) => {
    if (isModalOpen) {
      closeModal();
    }
    navigate(url);
  };

  return { navigateAndCloseModal };
}; 